import type {
  BridgeChartDrawing,
  BridgeChartDrawingExtensionState,
  BridgeChartDrawingHandle,
  BridgeChartDrawingHit,
  BridgeChartDrawingKind,
  BridgeChartDrawingMigrationContext,
  BridgeChartDrawingPoint,
  BridgeChartDrawingState
} from "../types/public";
import { fibonacciLevels, normalizeFibonacciLevels } from "./fibonacci";
import { migrationTransform, normalizeDrawingAnchors, type DrawingTransform } from "./marketCoordinates";

const DRAWING_KINDS = new Set<BridgeChartDrawingKind>([
  "horizontalLine",
  "verticalLine",
  "trendLine",
  "ray",
  "rectangle",
  "fibonacci"
]);

const EXTENSIONS: readonly BridgeChartDrawingExtensionState[] = Object.freeze([
  Object.freeze({ extension: "fibonacci", available: true, reason: null }),
  Object.freeze({ extension: "text", available: false, reason: "NotImplemented" }),
  Object.freeze({ extension: "measurement", available: false, reason: "NotImplemented" })
]);

interface Snapshot {
  drawings: BridgeChartDrawing[];
  selectedId: string | null;
}

export class DrawingStore {
  private drawingsValue: BridgeChartDrawing[] = [];
  private selectedIdValue: string | null = null;
  private activeToolValue: BridgeChartDrawingKind | null = null;
  private readonly undoStack: Snapshot[] = [];
  private nextId = 1;

  constructor(initial: readonly BridgeChartDrawing[] = [], private readonly transform?: () => DrawingTransform | null) {
    const ids = new Set<string>();
    this.drawingsValue = initial.map(drawing => {
      const normalized = normalizeDrawing(drawing);
      if (ids.has(normalized.id)) throw new TypeError(`BridgeChart drawing id '${normalized.id}' is duplicated.`);
      ids.add(normalized.id);
      return normalized;
    });
  }

  state(): BridgeChartDrawingState {
    return {
      drawings: this.drawingsValue.map(drawing => this.project(drawing) ?? cloneDrawing(drawing)),
      selectedId: this.selectedIdValue,
      activeTool: this.activeToolValue,
      extensions: EXTENSIONS.map(extension => ({ ...extension }))
    };
  }

  setTool(tool: BridgeChartDrawingKind | null): void {
    if (tool !== null && !DRAWING_KINDS.has(tool)) {
      throw new TypeError(`BridgeChart drawing tool '${String(tool)}' is unavailable.`);
    }
    this.activeToolValue = tool;
  }

  create(input: Omit<BridgeChartDrawing, "id"> & { id?: string }): BridgeChartDrawing {
    const id = input.id === undefined ? this.allocateId() : normalizeId(input.id);
    if (this.drawingsValue.some(drawing => drawing.id === id)) {
      throw new TypeError(`BridgeChart drawing id '${id}' is duplicated.`);
    }
    const drawing = normalizeDrawing({ ...input, id });
    this.remember();
    this.drawingsValue.push(drawing);
    this.selectedIdValue = drawing.id;
    return cloneDrawing(drawing);
  }

  select(id: string | null): void {
    if (id !== null && !this.drawingsValue.some(drawing => drawing.id === id)) {
      throw new TypeError(`BridgeChart drawing '${id}' is not present.`);
    }
    this.selectedIdValue = id;
  }

  move(id: string, delta: BridgeChartDrawingPoint): void {
    const drawing = this.requireDrawing(id);
    const normalizedDelta = normalizeDelta(delta);
    if (drawing.anchors) {
      const transform = this.requireTransform(drawing);
      const start = transform.project(drawing.anchors.start), end = transform.project(drawing.anchors.end);
      const anchors = transform.anchors({ x: start.x + delta.x, y: start.y + delta.y }, { x: end.x + delta.x, y: end.y + delta.y });
      this.remember(); drawing.anchors = anchors; this.selectedIdValue = id; return;
    }
    this.remember();
    drawing.start = translate(drawing.start, normalizedDelta);
    drawing.end = translate(drawing.end, normalizedDelta);
    this.selectedIdValue = id;
  }

  resize(id: string, handle: BridgeChartDrawingHandle, point: BridgeChartDrawingPoint): void {
    if (handle !== "start" && handle !== "end") throw new TypeError("BridgeChart drawing handle is invalid.");
    const drawing = this.requireDrawing(id);
    const normalized = normalizePoint(point);
    const anchors = drawing.anchors ? { ...drawing.anchors, [handle]: this.requireTransform(drawing).capture(normalized) } : undefined;
    this.remember();
    if (anchors) drawing.anchors = anchors;
    else drawing[handle] = normalized;
    this.selectedIdValue = id;
  }

  delete(id: string): void {
    const index = this.drawingsValue.findIndex(drawing => drawing.id === id);
    if (index < 0) throw new TypeError(`BridgeChart drawing '${id}' is not present.`);
    this.remember();
    this.drawingsValue.splice(index, 1);
    if (this.selectedIdValue === id) this.selectedIdValue = null;
  }

  clear(): void {
    if (this.drawingsValue.length === 0) return;
    this.remember();
    this.drawingsValue = [];
    this.selectedIdValue = null;
  }

  undo(): boolean {
    const previous = this.undoStack.pop();
    if (!previous) return false;
    this.drawingsValue = previous.drawings.map(cloneDrawing);
    this.selectedIdValue = previous.selectedId;
    return true;
  }

  hitTest(point: BridgeChartDrawingPoint, tolerance = 0.025): BridgeChartDrawingHit | null {
    const target = normalizePoint(point);
    if (!Number.isFinite(tolerance) || tolerance <= 0 || tolerance > 1) {
      throw new RangeError("BridgeChart drawing tolerance must be greater than 0 and at most 1.");
    }
    const hits = this.drawingsValue.flatMap((drawing, insertionIndex) => {
      const projected = this.project(drawing);
      return projected ? drawingHits(projected, target, tolerance).map(hit => ({ ...hit, insertionIndex })) : [];
    });
    hits.sort((a, b) => a.distance - b.distance || b.insertionIndex - a.insertionIndex || a.id.localeCompare(b.id));
    const winner = hits[0];
    return winner ? { id: winner.id, region: winner.region, distance: winner.distance } : null;
  }

  serialize(): string {
    if (this.drawingsValue.some(drawing => drawing.anchors)) return JSON.stringify({
      schemaVersion: "bridge.chart-drawings/2.0.0",
      drawings: this.drawingsValue.map(drawing => drawing.anchors
        ? { id: drawing.id, kind: drawing.kind, coordinateSpace: "market", anchors: normalizeDrawingAnchors(drawing.anchors), ...(drawing.levels ? { levels: [...drawing.levels] } : {}) }
        : { ...cloneDrawing(drawing), coordinateSpace: "viewport" })
    });
    return JSON.stringify({
      schemaVersion: "bridge.chart-drawings/1.0.0",
      drawings: this.drawingsValue.map(cloneDrawing)
    });
  }

  import(serialized: string, legacyContext?: BridgeChartDrawingMigrationContext): void {
    if (typeof serialized !== "string" || serialized.length > 1_048_576) throw new TypeError("Drawing layout is invalid or too large.");
    const input = JSON.parse(serialized);
    if (!input || Object.keys(input).some(key => key !== "schemaVersion" && key !== "drawings")
        || !["bridge.chart-drawings/1.0.0", "bridge.chart-drawings/2.0.0"].includes(input.schemaVersion)
        || !Array.isArray(input.drawings) || input.drawings.length > 1000) throw new TypeError("Unsupported drawing layout contract.");
    const legacy = legacyContext ? migrationTransform(legacyContext) : null;
    const ids = new Set<string>();
    const candidates = input.drawings.map((value: Record<string, unknown>) => {
      if (!value || typeof value !== "object") throw new TypeError("Invalid drawing layout entry.");
      if (typeof value.id !== "string") throw new TypeError("Drawing layout id must be a string.");
      const market = input.schemaVersion === "bridge.chart-drawings/2.0.0" && value.coordinateSpace === "market";
      if (market) normalizeDrawingAnchors(value.anchors as NonNullable<BridgeChartDrawing["anchors"]>);
      const allowed = market ? ["id", "kind", "coordinateSpace", "anchors", "levels"] : ["id", "kind", "start", "end", "levels", ...(input.schemaVersion.endsWith("2.0.0") ? ["coordinateSpace"] : [])];
      if (Object.keys(value).some(key => !allowed.includes(key)) || (!market && input.schemaVersion.endsWith("2.0.0") && value.coordinateSpace !== "viewport"))
        throw new TypeError("Unknown drawing layout field or coordinate space.");
      if (!market && !legacy) throw new TypeError("Legacy drawing migration requires its original viewport, dates and price basis; context is never inferred.");
      const drawing = normalizeDrawing(market
        ? { id: value.id, kind: value.kind, levels: value.levels, start: { x: 0, y: 0 }, end: { x: 0, y: 0 }, anchors: value.anchors } as BridgeChartDrawing
        : { id: value.id, kind: value.kind, levels: value.levels, start: value.start, end: value.end } as BridgeChartDrawing);
      if (!market) drawing.anchors = legacy!.anchors(drawing.start, drawing.end);
      if (ids.has(drawing.id)) throw new TypeError("Drawing layout contains duplicated ids.");
      ids.add(drawing.id); return drawing;
    });
    this.remember(); this.drawingsValue = candidates; this.selectedIdValue = null;
  }

  private requireTransform(drawing: BridgeChartDrawing): DrawingTransform {
    const transform = this.transform?.();
    if (!transform || !drawing.anchors || !transform.matches(drawing.anchors)) throw new TypeError("Drawing market context is unavailable or mismatched.");
    return transform;
  }

  private project(drawing: BridgeChartDrawing): BridgeChartDrawing | null {
    const copy = cloneDrawing(drawing);
    if (!copy.anchors) return copy;
    const transform = this.transform?.();
    if (!transform || !transform.matches(copy.anchors)) return null;
    const start = transform.project(copy.anchors.start), end = transform.project(copy.anchors.end);
    return { ...copy, start: { x: roundCoordinate(start.x), y: roundCoordinate(start.y) }, end: { x: roundCoordinate(end.x), y: roundCoordinate(end.y) } };
  }

  private requireDrawing(id: string): BridgeChartDrawing {
    const drawing = this.drawingsValue.find(candidate => candidate.id === id);
    if (!drawing) throw new TypeError(`BridgeChart drawing '${id}' is not present.`);
    return drawing;
  }

  private allocateId(): string {
    let id = "";
    do id = `drawing-${String(this.nextId++).padStart(4, "0")}`;
    while (this.drawingsValue.some(drawing => drawing.id === id));
    return id;
  }

  private remember(): void {
    this.undoStack.push({ drawings: this.drawingsValue.map(cloneDrawing), selectedId: this.selectedIdValue });
    if (this.undoStack.length > 100) this.undoStack.shift();
  }
}

function drawingHits(drawing: BridgeChartDrawing, point: BridgeChartDrawingPoint, tolerance: number): BridgeChartDrawingHit[] {
  const handles: BridgeChartDrawingHit[] = ([
    { id: drawing.id, region: "start", distance: distance(point, drawing.start) },
    { id: drawing.id, region: "end", distance: distance(point, drawing.end) }
  ] satisfies BridgeChartDrawingHit[]).filter(hit => hit.distance <= tolerance);
  if (handles.length) return handles;

  let bodyDistance: number;
  switch (drawing.kind) {
    case "horizontalLine": bodyDistance = Math.abs(point.y - drawing.start.y); break;
    case "verticalLine": bodyDistance = Math.abs(point.x - drawing.start.x); break;
    case "trendLine": bodyDistance = distanceToSegment(point, drawing.start, drawing.end); break;
    case "ray": bodyDistance = distanceToRay(point, drawing.start, drawing.end); break;
    case "rectangle": bodyDistance = distanceToRectangle(point, drawing.start, drawing.end); break;
    case "fibonacci": bodyDistance = distanceToFibonacci(point, drawing); break;
  }
  return bodyDistance <= tolerance ? [{ id: drawing.id, region: "body", distance: bodyDistance }] : [];
}

function normalizeDrawing(value: BridgeChartDrawing): BridgeChartDrawing {
  if (!DRAWING_KINDS.has(value.kind)) throw new TypeError(`BridgeChart drawing kind '${String(value.kind)}' is unavailable.`);
  if (value.kind !== "fibonacci" && value.levels !== undefined) throw new TypeError("BridgeChart Fibonacci levels require the fibonacci drawing kind.");
  const levels = value.kind === "fibonacci" ? normalizeFibonacciLevels(value.levels) : undefined;
  const anchors = value.anchors ? normalizeDrawingAnchors(value.anchors) : undefined;
  const point = anchors ? normalizeProjectedPoint : normalizePoint;
  return { id: normalizeId(value.id), kind: value.kind, start: point(value.start), end: point(value.end), ...(levels ? { levels } : {}), ...(anchors ? { anchors } : {}) };
}

function normalizeProjectedPoint(value: BridgeChartDrawingPoint): BridgeChartDrawingPoint {
  if (!value || !Number.isFinite(value.x) || !Number.isFinite(value.y)) throw new RangeError("Market drawing projections must be finite.");
  return { x: value.x, y: value.y };
}

function normalizeId(value: string): string {
  const id = String(value).trim();
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/.test(id)) throw new TypeError("BridgeChart drawing id is invalid.");
  return id;
}

function normalizePoint(value: BridgeChartDrawingPoint): BridgeChartDrawingPoint {
  if (!value || !Number.isFinite(value.x) || !Number.isFinite(value.y) || value.x < 0 || value.x > 1 || value.y < 0 || value.y > 1) {
    throw new RangeError("BridgeChart drawing points must contain finite normalized coordinates from 0 through 1.");
  }
  return { x: value.x, y: value.y };
}

function normalizeDelta(value: BridgeChartDrawingPoint): BridgeChartDrawingPoint {
  if (!value || !Number.isFinite(value.x) || !Number.isFinite(value.y) || Math.abs(value.x) > 1 || Math.abs(value.y) > 1) {
    throw new RangeError("BridgeChart drawing deltas must be finite and bounded from -1 through 1.");
  }
  return { x: value.x, y: value.y };
}

function cloneDrawing(value: BridgeChartDrawing): BridgeChartDrawing {
  return { id: value.id, kind: value.kind, start: { ...value.start }, end: { ...value.end }, ...(value.levels ? { levels: [...value.levels] } : {}), ...(value.anchors ? { anchors: normalizeDrawingAnchors(value.anchors) } : {}) };
}

function distanceToFibonacci(point: BridgeChartDrawingPoint, drawing: BridgeChartDrawing): number {
  const left = Math.min(drawing.start.x, drawing.end.x);
  const right = Math.max(drawing.start.x, drawing.end.x);
  if (point.x < left || point.x > right) return Math.min(distance(point, drawing.start), distance(point, drawing.end));
  return Math.min(...fibonacciLevels(drawing).map(level => {
    const y = drawing.start.y + (drawing.end.y - drawing.start.y) * level;
    return Math.abs(point.y - y);
  }));
}

function translate(point: BridgeChartDrawingPoint, delta: BridgeChartDrawingPoint): BridgeChartDrawingPoint {
  return { x: roundCoordinate(clamp(point.x + delta.x)), y: roundCoordinate(clamp(point.y + delta.y)) };
}

function distance(a: BridgeChartDrawingPoint, b: BridgeChartDrawingPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function distanceToSegment(point: BridgeChartDrawingPoint, start: BridgeChartDrawingPoint, end: BridgeChartDrawingPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distance(point, start);
  const projection = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared);
  return distance(point, { x: start.x + projection * dx, y: start.y + projection * dy });
}

function distanceToRay(point: BridgeChartDrawingPoint, start: BridgeChartDrawingPoint, end: BridgeChartDrawingPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distance(point, start);
  const projection = Math.max(0, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared);
  return distance(point, { x: start.x + projection * dx, y: start.y + projection * dy });
}

function distanceToRectangle(point: BridgeChartDrawingPoint, start: BridgeChartDrawingPoint, end: BridgeChartDrawingPoint): number {
  const left = Math.min(start.x, end.x);
  const right = Math.max(start.x, end.x);
  const top = Math.min(start.y, end.y);
  const bottom = Math.max(start.y, end.y);
  if (point.x >= left && point.x <= right && point.y >= top && point.y <= bottom) return 0;
  const nearest = { x: Math.max(left, Math.min(right, point.x)), y: Math.max(top, Math.min(bottom, point.y)) };
  return distance(point, nearest);
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function roundCoordinate(value: number): number {
  return Number(value.toFixed(12));
}
